import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { C, fmt } from "../theme";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Card from "../components/Card";
import ProfileAvatar from "../components/ProfileAvatar";
import { getProfilePhotoUrl } from "../services/profilePhoto";
import { db, storage } from "../firebase";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, getDoc, getDocs, collection, query, where, setDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { sha256Hex, formatTimestamp } from "../utils";
import { authedWrite } from "../utils/authedWrite";
import { useSessionContext } from "../context/SessionContext";
import { queuePendingVideo, queuePendingVideoFile } from "../services/offlineStore";
import { downloadToFile, IS_NATIVE } from "../services/videoFileStore";
import { uploadVideoToFacebookPage } from "../services/facebookUploadClient";
import { sendDirectorCertifiedVideoEvent } from "../services/tournamentEventWriters";
import { isR2Backend } from "../config/videoBackend";
import { uploadVideo as r2UploadVideo, getPlaybackUrl as r2GetPlaybackUrl } from "../services/r2Video";
import { publishCatchToYouTube } from "../services/youtubeShare";
import { getSubscriptionView, canUploadCatchVideo } from "../utils/featureGate";

const genCertId = () => {
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  return `T99-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
};

// Per-tier / per-source upload size caps (bytes). Mirror storage.rules and the
// backend validator (functions-python/app/services/r2_service.py).
const _GB = 1024 * 1024 * 1024;
const _MB = 1024 * 1024;
const VIDEO_SIZE_CAPS = {
  // `${isGoPro}:${isPro}` -> max bytes
  "false:false": 500 * _MB, // in-app recording, free angler
  "false:true": 5 * _GB,    // in-app recording, pro angler
  "true:false": 1 * _GB,    // GoPro clip, free angler
  "true:true": 10 * _GB,    // GoPro clip, pro angler
};

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "0 MB";
  if (bytes >= _GB) return `${(bytes / _GB).toFixed(bytes >= 10 * _GB ? 0 : 1)} GB`;
  return `${Math.max(1, Math.round(bytes / _MB))} MB`;
};

const MAP_OVERLAY_SIZE_KEY = "the99.videoMapOverlaySize";

// Watermark overlays sit on hard-coded dark backgrounds (rgba(0,0,0,*) and the
// Viewfinder gradient) regardless of light/dark theme. Use theme-independent
// colors drawn from the dark palette so the burned-in text remains legible in
// every theme. Do NOT swap these for C.white / C.muted (which invert in light
// mode and become unreadable on the dark overlay).
const OVERLAY = {
  text: "#FFFFFF",
  muted: "#B8C5D0",
  accent: "#3B82F6",
  gold: "#60A5FA",
  seafoam: "#2563EB",
  red: "#E03030",
  textShadow: "0 1px 2px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.55)",
};

function readMapOverlaySize() {
  try {
    const raw = localStorage.getItem(MAP_OVERLAY_SIZE_KEY);
    return raw === "hidden" || raw === "small" || raw === "large" ? raw : "large";
  } catch {
    return "large";
  }
}

function Viewfinder({ children }) {
  return (
    <div style={{ flex: 1, position: "relative", background: "#081a0e", overflow: "hidden" }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg,#0a1f0c 0%,#082818 50%,#0b3018 100%)",
        }}
      />
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.12 }}>
        <line x1="33%" y1="0" x2="33%" y2="100%" stroke="white" strokeWidth={0.5} />
        <line x1="66%" y1="0" x2="66%" y2="100%" stroke="white" strokeWidth={0.5} />
        <line x1="0" y1="33%" x2="100%" y2="33%" stroke="white" strokeWidth={0.5} />
        <line x1="0" y1="66%" x2="100%" y2="66%" stroke="white" strokeWidth={0.5} />
      </svg>
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.55 }}>
        <path d="M22 46 L22 22 L46 22" stroke={C.accent} strokeWidth={2.5} fill="none" strokeLinecap="round" />
        <path d="M329 22 L353 22 L353 46" stroke={C.accent} strokeWidth={2.5} fill="none" strokeLinecap="round" />
        <path d="M22 560 L22 584 L46 584" stroke={C.accent} strokeWidth={2.5} fill="none" strokeLinecap="round" />
        <path d="M329 584 L353 584 L353 560" stroke={C.accent} strokeWidth={2.5} fill="none" strokeLinecap="round" />
      </svg>
      {children}
    </div>
  );
}

function MetaStamp({ catchData, location, label = "AUTO-STAMPED ON RECORD", labelColor = OVERLAY.accent, borderColor = OVERLAY.accent + "55" }) {
  return (
    <div
      style={{
        position: "absolute",
        bottom: 14,
        left: 14,
        right: 14,
        background: "rgba(0,0,0,0.88)",
        borderRadius: 10,
        padding: "10px 12px",
        border: `1px solid ${borderColor}`,
        backdropFilter: "blur(4px)",
        textShadow: OVERLAY.textShadow,
      }}
    >
      <div style={{ fontSize: 8, color: labelColor, letterSpacing: 2, marginBottom: 5 }}>
        ● {label}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 0" }}>
        {Object.entries({
          SPECIES: catchData.species,
          SIZE: catchData.size,
          WEIGHT: catchData.weight,
          LURE: catchData.lure,
          GPS: location ? `${location.lat.toFixed(5)}°N · ${location.lng.toFixed(5)}°W` : "–",
          TIME: formatTimestamp(),
          TIDE: "Outgoing · 1.8hr",
          TEMP: "71°F · ↓Baro",
        }).map(([k, v]) => (
          <div
            key={k}
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "50%",
              fontSize: 9,
              color: OVERLAY.text,
            }}
          >
            <span style={{ color: OVERLAY.muted }}>{k}</span>
            <span style={{ fontWeight: 700, textAlign: "right" }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// YouTube glyph (red rounded rect + play triangle). `bg`/`tri` invert on the
// solid-red Post button so the mark reads on either background.
function YtIcon({ w = 22, bg = "#FF0000", tri = "#FFFFFF" }) {
  const h = Math.round((w * 20) / 28);
  return (
    <svg width={w} height={h} viewBox="0 0 28 20" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
      <rect width="28" height="20" rx="5" fill={bg} />
      <path d="M11 6l7 4-7 4V6z" fill={tri} />
    </svg>
  );
}

export default function VideoScreen({ onClose, onLogCatch, catchData, catchRef, location, userProfile, isOffline, isFacebookLoginUser = false, preloadedVideo = null, onBackToGoPro = null, trimMeta = null, tournamentApplicable = true }) {
  const { userId, registerPendingVideo } = useSessionContext();
  const [phase, setPhase] = useState(preloadedVideo ? "reviewing" : "preview");
  const [recTime, setRecTime] = useState(0);
  // Authoritative recorded duration (seconds), captured from the wall clock at
  // stop. MediaRecorder WebM blobs omit duration metadata, so the <video>
  // element reports Infinity/NaN — we use this instead for the review scrubber.
  const [recordedDurationSec, setRecordedDurationSec] = useState(null);
  const [reviewCurrentSec, setReviewCurrentSec] = useState(0);
  const [reviewDurationSec, setReviewDurationSec] = useState(0);
  const [reviewPlaying, setReviewPlaying] = useState(false);
  // Recording <video> starts hidden until its first frame is decoded. Without
  // this, the freshly-mounted element paints a distorted gray placeholder
  // (its default 300x150 box stretched by objectFit:cover) for a beat before
  // real camera frames arrive.
  const [recordingVideoReady, setRecordingVideoReady] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [certId, setCertId] = useState(genCertId());
  const [videoUrl, setVideoUrl] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState(null);
  const [previewBlocked, setPreviewBlocked] = useState(false);
  const [activePreviewStream, setActivePreviewStream] = useState(null);
  const [mapOverlaySize, setMapOverlaySize] = useState(readMapOverlaySize);
  const [timeTamperFlag, setTimeTamperFlag] = useState(false);
  const [timeDriftSeconds, setTimeDriftSeconds] = useState(null);
  const [firebaseUploadStatus, setFirebaseUploadStatus] = useState("not-started");
  const [videoViewCount, setVideoViewCount] = useState(0);
  // New anglers (non-Pro) may post 1 catch video per calendar month; catches
  // stay unlimited. This holds their video count for the current month.
  const [monthlyVideoCount, setMonthlyVideoCount] = useState(0);
  // Post the certified clip to THE 99's own Facebook Page. Certify-first: a
  // user-confirmed action from the certified-clip screen, gated by the same
  // shareVideos preference as YouTube.
  const [facebookShareStatus, setFacebookShareStatus] = useState("idle"); // idle|posting|confirmed|failed
  const [facebookShareError, setFacebookShareError] = useState("");
  const [facebookPermalink, setFacebookPermalink] = useState("");
  // YouTube publishing is opt-in (userPreferences.shareVideos) AND user-confirmed:
  // after a clip certifies, opted-in anglers are prompted to post it to THE 99's
  // channel. Nothing uploads to YouTube automatically.
  const [youtubeOptIn, setYoutubeOptIn] = useState(false);
  const [youtubeShareStatus, setYoutubeShareStatus] = useState("idle"); // idle|posting|confirmed|failed|skipped
  const [youtubeShareError, setYoutubeShareError] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  // Elapsed seconds while a YouTube post is in flight — drives the progress
  // indicator's reassuring "…keep this screen open" timer.
  const [youtubePostingElapsed, setYoutubePostingElapsed] = useState(0);
  // When a user taps "Upload & Certify" without a GPS fix, surface an in-app
  // confirmation so they don't silently upload a clip that can't be plotted on
  // the catch map. Confirming proceeds with the actual upload.
  const [showNoGpsConfirm, setShowNoGpsConfirm] = useState(false);
  // Optical/digital zoom control. `zoomCaps` mirrors the active video track's
  // MediaTrackCapabilities.zoom (min/max/step). When null, the device/browser
  // doesn't support programmatic zoom so the +/- control is hidden.
  const [zoomCaps, setZoomCaps] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordedBlobsRef = useRef([]);
  const previewVideoRef = useRef(null);  const recordingVideoRef = useRef(null);
  const reviewVideoRef = useRef(null);
  const recStartMsRef = useRef(0);

  const fallbackCertLink = `the99.app/cert/${certId}`;

  // Derive content type / extension from preloaded source (e.g. GoPro mp4) or default to webm.
  const sourceMimeType = preloadedVideo?.mimeType || preloadedVideo?.blob?.type || "video/webm";
  const sourceExt = sourceMimeType.includes("mp4")
    ? "mp4"
    : sourceMimeType.includes("quicktime") || sourceMimeType.includes("mov")
      ? "mov"
      : "webm";
  const sourceLabel = preloadedVideo?.source || null; // e.g. "gopro"

  // Upload size cap for this clip, keyed on source (GoPro vs in-app) and the
  // angler's subscription tier. Mirrors storage.rules + the backend validator.
  const isGoProSource = sourceLabel === "gopro";
  const isProAngler = useMemo(() => getSubscriptionView(userProfile).isProActive, [userProfile]);
  const uploadSizeCapBytes = VIDEO_SIZE_CAPS[`${isGoProSource}:${isProAngler}`];
  const monthlyLimitReached = !isProAngler && !canUploadCatchVideo(userProfile, monthlyVideoCount);
  const clipSizeBytes = useMemo(() => {
    if (preloadedVideo?.blob?.size) return preloadedVideo.blob.size;
    const parts = recordedBlobsRef.current || [];
    return parts.reduce((sum, part) => sum + (part?.size || 0), 0);
    // `phase` recomputes this once recording stops and the blob parts land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedVideo, phase]);
  const clipOverLimit = clipSizeBytes > uploadSizeCapBytes;

  // Seed reviewing phase with a preloaded blob (e.g. a GoPro clip) so the
  // user lands directly on the Confirm & Certify screen.
  useEffect(() => {
    if (!preloadedVideo?.blob) return;
    recordedBlobsRef.current = [preloadedVideo.blob];
    // Prefer a direct, streamable file URL for playback when the source provides
    // one (e.g. GoPro). Building an object URL from the entire blob loads the
    // whole clip into WebView memory, which for large GoPro files can fail and
    // show a gray, unplayable frame. Fall back to an object URL otherwise.
    const url = preloadedVideo.previewUrl || URL.createObjectURL(preloadedVideo.blob);
    setVideoUrl(url);
    setPhase("reviewing");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Interactive map helpers
  const mapCenter = location ? [location.lat, location.lng] : [29.5, -88.5];
  const pulseIcon = useMemo(() => L.divIcon({
    className: "",
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${C.accent};border:2.5px solid white;box-shadow:0 0 14px ${C.accent}cc;"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  }), []);

  useEffect(() => {
    try {
      localStorage.setItem(MAP_OVERLAY_SIZE_KEY, mapOverlaySize);
    } catch {}
  }, [mapOverlaySize]);

  // Listen for Cloud Function writing timeTamperFlag back to cert doc
  useEffect(() => {
    if (phase !== "certified") return;
    const unsub = onSnapshot(doc(db, "certificates", certId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (typeof data.timeTamperFlag === "boolean") setTimeTamperFlag(data.timeTamperFlag);
      if (typeof data.timeDriftSeconds === "number") setTimeDriftSeconds(data.timeDriftSeconds);
    });
    return () => unsub();
  }, [phase, certId]);

  // Live view count for this certified clip (maintained server-side).
  useEffect(() => {
    if (phase !== "certified") return;
    const unsub = onSnapshot(doc(db, "videos", certId), (snap) => {
      setVideoViewCount(snap.exists() ? Number(snap.data()?.viewCount || 0) : 0);
    });
    return () => unsub();
  }, [phase, certId]);

  // Load the angler's video-sharing opt-in so we know whether to prompt them to
  // publish this certified clip to THE 99's YouTube channel.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "userPreferences", userId));
        if (!cancelled && snap.exists()) {
          setYoutubeOptIn(Boolean(snap.data()?.shareVideos));
        }
      } catch (err) {
        console.warn("[Video] failed to load shareVideos opt-in:", err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Count this angler's videos certified in the current calendar month so we
  // can enforce the new-angler 1-per-month limit. Pro anglers are unlimited, so
  // skip the read for them.
  useEffect(() => {
    if (!userId || isProAngler) {
      setMonthlyVideoCount(0);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "videos"), where("userId", "==", userId)));
        const now = new Date();
        const monthStartMs = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        let count = 0;
        snap.forEach((d) => {
          const created = d.data()?.createdAt;
          const ms = created?.toMillis?.() ?? (typeof created?.seconds === "number" ? created.seconds * 1000 : null);
          if (ms != null && ms >= monthStartMs) count += 1;
        });
        if (!cancelled) setMonthlyVideoCount(count);
      } catch (err) {
        console.warn("[Video] monthly video count failed:", err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, isProAngler]);

  const handlePublishToYouTube = React.useCallback(async () => {
    if (!certId) return;
    if (!isProAngler) {
      setYoutubeShareStatus("failed");
      setYoutubeShareError("Posting to YouTube is a Pro feature. Upgrade to Pro to share.");
      return;
    }
    setYoutubeShareStatus("posting");
    setYoutubeShareError("");
    try {
      const res = await publishCatchToYouTube(certId);
      setYoutubeUrl(res.url || "");
      setYoutubeShareStatus("confirmed");
    } catch (err) {
      setYoutubeShareStatus("failed");
      setYoutubeShareError(err?.message || "YouTube upload failed. Please try again.");
    }
  }, [certId, isProAngler]);

  // Certify-first: post the already-certified clip to THE 99's Facebook Page.
  // The backend resolves the video URL from videos/{certId}; we send only
  // certId (+ a title/description). User-confirmed, gated by shareVideos.
  const handlePublishToFacebook = React.useCallback(async () => {
    if (!certId) return;
    if (!isProAngler) {
      setFacebookShareStatus("failed");
      setFacebookShareError("Posting to Facebook is a Pro feature. Upgrade to Pro to share.");
      return;
    }
    setFacebookShareStatus("posting");
    setFacebookShareError("");
    try {
      const title = `${catchData?.species || "Catch"} - ${catchData?.size || ""} ${catchData?.weight || ""}`.trim();
      const description = [
        "Uploaded from The 99",
        catchData?.lure ? `Lure: ${catchData.lure}` : "",
      ]
        .filter(Boolean)
        .join(" | ");
      const res = await uploadVideoToFacebookPage({ certId, title, description });
      setFacebookPermalink(res?.permalinkUrl || "");
      setFacebookShareStatus("confirmed");
    } catch (err) {
      setFacebookShareStatus("failed");
      setFacebookShareError(err?.message || "Facebook upload failed. Please try again.");
    }
  }, [certId, catchData, isProAngler]);

  // Tick an elapsed-seconds counter while a YouTube post is uploading so the
  // progress indicator can reassure the angler during the server-side upload.
  useEffect(() => {
    if (youtubeShareStatus !== "posting") {
      setYoutubePostingElapsed(0);
      return undefined;
    }
    const startedAt = Date.now();
    setYoutubePostingElapsed(0);
    const id = setInterval(() => {
      setYoutubePostingElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [youtubeShareStatus]);

  const mapOverlayStyle = useMemo(() => {
    if (mapOverlaySize === "hidden") {
      return { display: "none" };
    }
    if (mapOverlaySize === "small") {
      return {
        position: "absolute",
        top: 74,
        right: 12,
        width: 152,
        height: 152,
        zIndex: -1,
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.28)",
        boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
      };
    }
    return { position: "absolute", inset: 0, zIndex: -1 };
  }, [mapOverlaySize]);

  function RecenterMap({ lat, lng }) {
    const map = useMap();
    useEffect(() => {
      if (lat && lng) map.setView([lat, lng]);
    }, [lat, lng, map]);
    return null;
  }

  function MapResizeController({ overlaySize }) {
    const map = useMap();
    useEffect(() => {
      const raf = requestAnimationFrame(() => {
        map.invalidateSize();
      });
      return () => cancelAnimationFrame(raf);
    }, [overlaySize, map]);
    return null;
  }

  const secureVideoLink = useMemo(() => {
    const baseLink = downloadUrl || fallbackCertLink;

    const queryParams = new URLSearchParams();
    if (catchData?.species && catchData.species !== "—") queryParams.set("species", catchData.species);
    if (catchData?.lure && catchData.lure !== "—") queryParams.set("lure", catchData.lure);
    if (catchData?.weight && catchData.weight !== "—") queryParams.set("weight", catchData.weight);
    if (catchData?.size && catchData.size !== "—") queryParams.set("length", catchData.size);

    const query = queryParams.toString();
    if (!query) {
      return baseLink;
    }

    if (baseLink.includes("://")) {
      try {
        const parsed = new URL(baseLink);
        queryParams.forEach((value, key) => parsed.searchParams.set(key, value));
        return parsed.toString();
      } catch {
        return `${baseLink}${baseLink.includes("?") ? "&" : "?"}${query}`;
      }
    }

    return `${baseLink}?${query}`;
  }, [downloadUrl, fallbackCertLink, catchData]);


  useEffect(() => {
    if (phase !== "preview") return;
    if (preloadedVideo) return;
    const videoEl = previewVideoRef.current;
    const stream = activePreviewStream;
    if (!videoEl || !stream) return;

    videoEl.src = "";
    videoEl.srcObject = stream;
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.autoplay = true;
    videoEl
      .play()
      .then(() => setPreviewBlocked(false))
      .catch(() => {
        setPreviewBlocked(true);
      });
  }, [phase, activePreviewStream, preloadedVideo]);

  useEffect(() => {
    if (phase !== "preview") return;
    if (preloadedVideo) return;
    if (activePreviewStream) return;
    if (!navigator.mediaDevices?.getUserMedia) return;

    let cancelled = false;
    const startPreviewStream = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        setActivePreviewStream(stream);
      } catch (err) {
        console.warn("Preview stream unavailable:", err);
      }
    };

    startPreviewStream();
    return () => {
      cancelled = true;
    };
  }, [phase, activePreviewStream, preloadedVideo]);

  useEffect(() => {
    if (phase === "recording") {
      timerRef.current = setInterval(() => setRecTime((t) => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  useEffect(() => {
    if (phase === "reviewing" && reviewVideoRef.current && videoUrl) {
      const el = reviewVideoRef.current;
      el.srcObject = null;
      el.src = videoUrl;

      // MediaRecorder WebM clips omit duration metadata, so el.duration is
      // Infinity/NaN. Rather than force a full-file seek (which spins/"clocks"
      // on long clips), drive a custom scrubber from the known recorded
      // duration, falling back to el.duration for sources that report it
      // correctly (e.g. GoPro MP4).
      const resolveDuration = () => {
        const d = el.duration;
        if (Number.isFinite(d) && d > 0) return d;
        if (recordedDurationSec && recordedDurationSec > 0) return recordedDurationSec;
        return 0;
      };
      const syncFromElement = () => {
        setReviewCurrentSec(el.currentTime || 0);
        const d = el.duration;
        if (Number.isFinite(d) && d > 0) setReviewDurationSec(d);
      };

      // Poll currentTime via requestAnimationFrame while playing. Android's
      // WebView fires `timeupdate` sparsely — or not at all — for videos
      // streamed through the Capacitor file bridge (e.g. GoPro MP4s), which
      // left the review timer frozen at 00:00. Polling keeps it accurate.
      let rafId = null;
      const pump = () => {
        syncFromElement();
        rafId = requestAnimationFrame(pump);
      };
      const startPump = () => { if (rafId == null) rafId = requestAnimationFrame(pump); };
      const stopPump = () => {
        if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
        syncFromElement();
      };

      const onLoaded = () => setReviewDurationSec(resolveDuration());
      const onTime = syncFromElement;
      const onPlay = () => { setReviewPlaying(true); startPump(); };
      const onPause = () => { setReviewPlaying(false); stopPump(); };
      const onEnded = () => { setReviewPlaying(false); stopPump(); };

      el.addEventListener("loadedmetadata", onLoaded);
      el.addEventListener("durationchange", onLoaded);
      el.addEventListener("timeupdate", onTime);
      el.addEventListener("play", onPlay);
      el.addEventListener("pause", onPause);
      el.addEventListener("ended", onEnded);

      setReviewCurrentSec(0);
      setReviewDurationSec(resolveDuration());

      el.play().catch(() => {
        setPreviewBlocked(true);
      });

      return () => {
        stopPump();
        el.removeEventListener("loadedmetadata", onLoaded);
        el.removeEventListener("durationchange", onLoaded);
        el.removeEventListener("timeupdate", onTime);
        el.removeEventListener("play", onPlay);
        el.removeEventListener("pause", onPause);
        el.removeEventListener("ended", onEnded);
      };
    }
    return undefined;
  }, [phase, videoUrl, recordedDurationSec]);

  useEffect(() => {
    if (phase !== "recording") return;
    const videoEl = recordingVideoRef.current;
    const stream = activePreviewStream;
    if (!videoEl || !stream) return;

    videoEl.src = "";
    videoEl.srcObject = stream;
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.autoplay = true;
    videoEl
      .play()
      .then(() => setPreviewBlocked(false))
      .catch(() => {
        setPreviewBlocked(true);
      });
  }, [phase, activePreviewStream]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      setActivePreviewStream(null);
      if (videoUrl) {
        if (typeof videoUrl === "string" && videoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(videoUrl);
        }
      }
    };
  }, [videoUrl]);

  // Detect the active camera's zoom capabilities whenever a live stream starts.
  // Not all devices/browsers expose `zoom` in MediaTrackCapabilities; when it's
  // absent we leave zoomCaps null so the +/- control stays hidden.
  useEffect(() => {
    const track = activePreviewStream?.getVideoTracks?.()[0];
    if (!track || typeof track.getCapabilities !== "function") {
      setZoomCaps(null);
      return;
    }
    const caps = track.getCapabilities();
    if (caps && typeof caps.zoom === "object" && caps.zoom && "max" in caps.zoom) {
      const min = typeof caps.zoom.min === "number" ? caps.zoom.min : 1;
      const max = typeof caps.zoom.max === "number" ? caps.zoom.max : min;
      const step = typeof caps.zoom.step === "number" && caps.zoom.step > 0 ? caps.zoom.step : (max - min) / 10 || 0.1;
      if (max > min) {
        setZoomCaps({ min, max, step });
        const settings = track.getSettings?.();
        const current = typeof settings?.zoom === "number" ? settings.zoom : min;
        setZoomLevel(current);
        return;
      }
    }
    setZoomCaps(null);
  }, [activePreviewStream]);

  // Apply a new zoom value to the live video track and reflect it in state.
  const applyZoom = async (next) => {
    if (!zoomCaps) return;
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track || typeof track.applyConstraints !== "function") return;
    const clamped = Math.min(zoomCaps.max, Math.max(zoomCaps.min, next));
    try {
      await track.applyConstraints({ advanced: [{ zoom: clamped }] });
      setZoomLevel(clamped);
    } catch (err) {
      console.warn("Zoom not applied:", err);
    }
  };

  // Vertical +/- zoom control overlaid on the live camera. Renders nothing when
  // the device doesn't support programmatic zoom. Shared by preview + recording.
  const renderZoomControls = () => {
    if (!zoomCaps) return null;
    const step = zoomCaps.step * 4 || zoomCaps.step;
    const atMin = zoomLevel <= zoomCaps.min + 1e-6;
    const atMax = zoomLevel >= zoomCaps.max - 1e-6;
    const btnStyle = (disabled) => ({
      width: 44,
      height: 44,
      borderRadius: "50%",
      border: "1px solid #ffffff40",
      background: "rgba(0,0,0,0.55)",
      color: "#fff",
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.35 : 1,
      WebkitTapHighlightColor: "transparent",
    });
    return (
      <div
        style={{
          position: "absolute",
          right: 14,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1002,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <button
          type="button"
          aria-label="Zoom in"
          data-testid="video-zoom-in"
          disabled={atMax}
          onClick={() => applyZoom(zoomLevel + step)}
          style={btnStyle(atMax)}
        >
          +
        </button>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "#fff",
            background: "rgba(0,0,0,0.55)",
            borderRadius: 999,
            padding: "3px 8px",
            textShadow: "0 1px 4px rgba(0,0,0,0.9)",
          }}
        >
          {`${(zoomLevel / zoomCaps.min).toFixed(1)}×`}
        </div>
        <button
          type="button"
          aria-label="Zoom out"
          data-testid="video-zoom-out"
          disabled={atMin}
          onClick={() => applyZoom(zoomLevel - step)}
          style={btnStyle(atMin)}
        >
          −
        </button>
      </div>
    );
  };

  const enablePreview = async () => {
    const activeVideoEl =
      phase === "recording"
        ? recordingVideoRef.current
        : phase === "preview"
        ? previewVideoRef.current
        : reviewVideoRef.current;
    if (!activeVideoEl) return;
    try {
      await activeVideoEl.play();
      setPreviewBlocked(false);
    } catch {
      setPreviewBlocked(true);
    }
  };

  const startRecording = async () => {
    setError(null);
    setCertId(genCertId());
    setUploadPct(0);
    setDownloadUrl(null);
    setPreviewBlocked(false);
    setRecordingVideoReady(false);
    setFirebaseUploadStatus("not-started");
    setFacebookShareStatus("idle");
    setFacebookShareError("");
    setFacebookPermalink("");
    setYoutubeShareStatus("idle");
    setYoutubeShareError("");
    setYoutubeUrl("");

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Video recording is not supported by this browser.");
      return;
    }

    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setActivePreviewStream(null);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: true,
      });
      streamRef.current = stream;
      setActivePreviewStream(stream);
      recordedBlobsRef.current = [];

      const options = { mimeType: "video/webm; codecs=vp8,opus" };
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedBlobsRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedBlobsRef.current, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const durSec = recStartMsRef.current
          ? Math.max(1, Math.round((Date.now() - recStartMsRef.current) / 1000))
          : 0;
        setRecordedDurationSec(durSec);
        setVideoUrl(url);
        setPhase("reviewing");
      };

      recorder.start(250);
      recStartMsRef.current = Date.now();

      if (recordingVideoRef.current) {
        recordingVideoRef.current.srcObject = stream;
        recordingVideoRef.current.src = "";
        recordingVideoRef.current.muted = true;
        recordingVideoRef.current.playsInline = true;
        recordingVideoRef.current.autoplay = true;
        recordingVideoRef.current
          .play()
          .then(() => setPreviewBlocked(false))
          .catch(() => {
            // Autoplay may be blocked; show tap-to-enable overlay.
            setPreviewBlocked(true);
          });
      }

      setRecTime(0);
      setPhase("recording");
    } catch (err) {
      console.error(err);
      setError("Unable to access camera/microphone. Please allow permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop(); // onstop handler builds the blob and sets phase to "reviewing"
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    setActivePreviewStream(null);
  };

  const toggleReviewPlay = () => {
    const el = reviewVideoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  const seekReviewFromEvent = (event) => {
    const el = reviewVideoRef.current;
    if (!el || !reviewDurationSec) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const clientX = event.clientX ?? event.touches?.[0]?.clientX;
    if (clientX == null) return;
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const target = frac * reviewDurationSec;
    try { el.currentTime = target; } catch {}
    setReviewCurrentSec(target);
  };

  const uploadRecording = async () => {
    if (!videoUrl) {
      setError("No recorded video found. Please record a video first.");
      return;
    }

    if (!recordedBlobsRef.current || recordedBlobsRef.current.length === 0) {
      setError("No recorded data available. Please record a video again before uploading.");
      setPhase("preview");
      return;
    }

    // Start server clock ping in parallel with later blob work
    const clientTimestampMs = Date.now();
    const clockPingPromise = (async () => {
      try {
        const pingRef = doc(db, "_clockPing", userId || "anon");
        await setDoc(pingRef, { t: serverTimestamp() });
        const pingSnap = await getDoc(pingRef);
        const serverMs = pingSnap.data()?.t?.toMillis?.();
        if (typeof serverMs === "number") {
          return Math.abs(serverMs - clientTimestampMs) / 1000;
        }
      } catch {}
      return null;
    })();

    const blob = new Blob(recordedBlobsRef.current, { type: sourceMimeType });

    // New anglers (non-Pro) may post one catch video per calendar month. They
    // can still log unlimited catches — only the video upload is limited.
    if (monthlyLimitReached) {
      setError(
        "New anglers can post one catch video per month. You can keep logging catches — become a Pro Angler to post more videos."
      );
      setPhase("reviewing");
      return;
    }

    // Enforce the per-tier / per-source upload size cap before doing any work.
    if (blob.size > uploadSizeCapBytes) {
      const limitLabel = formatBytes(uploadSizeCapBytes);
      setError(
        isProAngler
          ? `This ${isGoProSource ? "GoPro clip" : "video"} is ${formatBytes(blob.size)}, over the ${limitLabel} limit. Please trim it and try again.`
          : `This ${isGoProSource ? "GoPro clip" : "video"} is ${formatBytes(blob.size)}, over the ${limitLabel} limit. Upgrade to Pro for uploads up to ${formatBytes(VIDEO_SIZE_CAPS[`${isGoProSource}:true`])}.`
      );
      setPhase("reviewing");
      return;
    }

    // ── Offline path: save to IndexedDB and show saved state ──────────────
    if (isOffline) {
      try {
        setPhase("uploading");
        const fileHash = await sha256Hex(blob);
        const metadata = {
          certId,
          hash: fileHash,
          timestamp: formatTimestamp(),
          certificateMeta: {
            certId,
            createdAt: null,
            timestamp: formatTimestamp(),
            catch: catchData,
            catchRef: catchRef?.catchId && catchRef?.sessionId
              ? { userId, sessionId: catchRef.sessionId, catchId: catchRef.catchId }
              : null,
            gps: location ? { lat: location.lat, lng: location.lng } : null,
            storagePath: `videos/${userId}/${certId}.${sourceExt}`,
            trim: trimMeta || null,
          },
          videoMeta: {
            certId,
            userId,
            role: userProfile?.role || "",
            tournamentId: tournamentApplicable ? (userProfile?.tournamentId || "") : "",
            tournamentName: tournamentApplicable ? (userProfile?.tournamentName || "") : "",
            teamId: tournamentApplicable ? (userProfile?.teamId || "") : "",
            teamName: tournamentApplicable ? (userProfile?.teamName || "") : "",
            source: sourceLabel || "live",
            catch: catchData || null,
            gps: location ? { lat: location.lat, lng: location.lng } : null,
            storagePath: `videos/${userId}/${certId}.${sourceExt}`,
            certificateRef: certId,
            catchRef: catchRef?.catchId && catchRef?.sessionId
              ? { userId, sessionId: catchRef.sessionId, catchId: catchRef.catchId }
              : null,
            trim: trimMeta || null,
            goproClipName: preloadedVideo?.goproClipName || null,
          },
          directorEventMeta: tournamentApplicable ? {
            certId,
            tournamentId: userProfile?.tournamentId || "",
            tournamentName: userProfile?.tournamentName || "",
            teamId: userProfile?.teamId || "",
            teamName: userProfile?.teamName || "",
            userId,
            catchData: catchData || null,
          } : null,
        };

        // Large clips (native, with an on-disk source such as GoPro MP4s):
        // stage the file on the device filesystem and queue a *reference*
        // instead of a multi-GB Blob in IndexedDB. Streams to disk with no
        // heap materialization. Falls back to the proven blob queue on any
        // failure or on web.
        let queuedAsFile = false;
        const sourceFileUrl = preloadedVideo?.previewUrl || null;
        if (IS_NATIVE && sourceFileUrl) {
          try {
            const file = await downloadToFile({ certId, url: sourceFileUrl });
            if (!file?.size) file.size = blob.size;
            await queuePendingVideoFile({ certId, file, metadata });
            queuedAsFile = true;
          } catch (stageErr) {
            console.warn("[VideoScreen] filesystem staging failed; falling back to blob queue:", stageErr?.message || stageErr);
          }
        }
        if (!queuedAsFile) {
          await queuePendingVideo({ certId, blob, metadata });
        }
        setPhase("saved-offline");
      } catch (err) {
        console.error("Offline queue failed:", err);
        setError("Failed to save video offline. Please try again.");
        setPhase("reviewing");
      }
      return;
    }

    setPhase("uploading");
    setFacebookShareError("");
    setFirebaseUploadStatus("uploading");

    if (!userId) {
      setError("You must be signed in to upload a video.");
      setPhase("reviewing");
      return;
    }

    try {
      const fileHash = await sha256Hex(blob);

      const useR2 = isR2Backend();
      let storagePath;
      let ref = null;
      if (useR2) {
        // Cloudflare R2: presigned PUT (or multipart for large clips). The
        // backend derives the key from uid + certId + content type.
        const { key } = await r2UploadVideo({
          certId,
          blob,
          contentType: sourceMimeType,
          source: sourceLabel || "live",
          onProgress: (loaded, total) => {
            if (total) setUploadPct(Math.round((loaded / total) * 100));
          },
        });
        storagePath = key;
      } else {
        storagePath = `videos/${userId}/${certId}.${sourceExt}`;
        ref = storageRef(storage, storagePath);
        const uploadTask = uploadBytesResumable(ref, blob, {
          contentType: sourceMimeType,
          customMetadata: {
            certId,
            hash: fileHash,
            timestamp: formatTimestamp(),
            ...(sourceLabel ? { source: sourceLabel } : {}),
            ...(preloadedVideo?.fileName ? { originalName: preloadedVideo.fileName } : {}),
          },
        });

        // Explicitly await completion via observer callbacks for deterministic behavior in dev and prod.
        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              setUploadPct(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
            },
            (uploadErr) => {
              reject(uploadErr);
            },
            () => {
              resolve();
            }
          );
        });
      }

      // Safety fallback: once upload completes, always move to certified view immediately.
      setFirebaseUploadStatus("confirmed");
      setPhase("certified");
      // Notify the source (e.g. GoPro screen) that this clip is now genuinely
      // uploaded, so it can mark it as done. Done here — after the upload
      // actually confirms — rather than optimistically at hand-off, otherwise a
      // clip the user backs out of would be wrongly struck through.
      try { preloadedVideo?.onUploaded?.(); } catch (_) {}

      // Await clock drift result (started in parallel with upload)
      const driftSeconds = await clockPingPromise;
      if (typeof driftSeconds === "number") {
        setTimeDriftSeconds(driftSeconds);
        if (driftSeconds > 300) {
          setError("Device clock is significantly out of sync. Certification blocked to protect integrity. Please correct your system time and try again.");
          setPhase("reviewing");
          return;
        }
      }

      // Continue post-upload metadata work in background.
      try {
        // Minting a playback URL is best-effort and must NOT gate the metadata
        // writes below. The durable identifier is `storagePath`; readers mint
        // a fresh presigned GET on demand (R2) or already hold a durable URL
        // (Firebase). If this throws (e.g. a transient presign hiccup), we
        // still persist the certificate/videos/catch records so the clip is
        // never silently orphaned.
        let url = "";
        try {
          url = (useR2 ? await r2GetPlaybackUrl(storagePath) : await getDownloadURL(ref)) || "";
          if (url) setDownloadUrl(url);
        } catch (urlErr) {
          console.warn("[Video] failed to mint playback URL (non-fatal; readers resolve from storagePath):", urlErr);
        }

        // Phase 7: on R2 the presigned GET is short-lived, so it must NOT be
        // persisted as a durable field. Readers re-mint from `storagePath` on
        // demand. On Firebase the downloadUrl is durable and stored as-is.
        const durableUrl = useR2 ? "" : url;

        let resolvedGps =
          typeof location?.lat === "number" && typeof location?.lng === "number"
            ? { lat: location.lat, lng: location.lng }
            : null;

        if (!resolvedGps && catchRef?.catchId && catchRef?.sessionId && userId) {
          try {
            const catchSnap = await getDoc(
              doc(db, "users", userId, "sessions", catchRef.sessionId, "catches", catchRef.catchId)
            );
            const catchDataSnap = catchSnap.exists() ? catchSnap.data() : null;
            if (
              typeof catchDataSnap?.location?.lat === "number" &&
              typeof catchDataSnap?.location?.lng === "number"
            ) {
              resolvedGps = {
                lat: catchDataSnap.location.lat,
                lng: catchDataSnap.location.lng,
              };
            }
          } catch (gpsLookupErr) {
            console.warn("[Video] failed GPS fallback lookup:", gpsLookupErr);
          }
        }

        let profileMeta = userProfile || null;
        if (!profileMeta && userId) {
          try {
            const userSnap = await getDoc(doc(db, "users", userId));
            profileMeta = userSnap.exists() ? userSnap.data() : null;
          } catch {}
        }

        try {
          await authedWrite(() => setDoc(doc(db, "certificates", certId), {
            certId,
            createdAt: serverTimestamp(),
            timestamp: formatTimestamp(),
            clientTimestampMs,
            catch: catchData,
            catchRef: catchRef?.catchId && catchRef?.sessionId
              ? {
                  userId,
                  sessionId: catchRef.sessionId,
                  catchId: catchRef.catchId,
                }
              : null,
            gps: resolvedGps,
            hash: fileHash,
            storagePath,
            downloadUrl: durableUrl,
            trim: trimMeta || null,
          }));

          if (catchRef?.catchId && catchRef?.sessionId) {
            await authedWrite(() => setDoc(
              doc(db, "users", userId, "sessions", catchRef.sessionId, "catches", catchRef.catchId),
              {
                status: "certified",
                videoCertified: true,
                certificateId: certId,
                certifiedAt: serverTimestamp(),
                videoStoragePath: storagePath,
                videoDownloadUrl: durableUrl,
                ...(resolvedGps ? { location: resolvedGps } : {}),
              },
              { merge: true }
            ));
          }

          await authedWrite(() => setDoc(
            doc(db, "videos", certId),
            {
              certId,
              createdAt: serverTimestamp(),
              userId,
              role: profileMeta?.role || "",
              tournamentId: tournamentApplicable ? (profileMeta?.tournamentId || "") : "",
              tournamentName: tournamentApplicable ? (profileMeta?.tournamentName || "") : "",
              teamId: tournamentApplicable ? (profileMeta?.teamId || "") : "",
              teamName: tournamentApplicable ? (profileMeta?.teamName || "") : "",
              catch: catchData || null,
              gps: resolvedGps,
              storagePath,
              downloadUrl: durableUrl,
              certificateRef: certId,
              source: sourceLabel || "live",
              originalName: preloadedVideo?.fileName || null,
              goproClipName: preloadedVideo?.goproClipName || null,
              catchRef: catchRef?.catchId && catchRef?.sessionId
                ? {
                    userId,
                    sessionId: catchRef.sessionId,
                    catchId: catchRef.catchId,
                  }
                : null,
              trim: trimMeta || null,
            },
            { merge: true }
          ));

          if (tournamentApplicable) {
            await sendDirectorCertifiedVideoEvent({
              certId,
              tournamentId: profileMeta?.tournamentId || "",
              tournamentName: profileMeta?.tournamentName || "",
              teamId: profileMeta?.teamId || "",
              teamName: profileMeta?.teamName || "",
              userId,
              catchData,
              downloadUrl: url,
              source: "live-upload",
            });
          }

          // If this clip wasn't tied to an existing catch, register it so the
          // NEXT catch logged through any path gets linked to it. Without this,
          // a video proof recorded before its catch (and finished via "Return
          // to Recording" rather than "Log Catch") would stay orphaned and
          // never appear in the trip summary.
          if (!(catchRef?.catchId && catchRef?.sessionId)) {
            try { registerPendingVideo?.(certId); } catch (_) {}
          }
        } catch (firestoreErr) {
          console.error("Firestore write failed (non-fatal):", firestoreErr);
        }
      } catch (postUploadErr) {
        console.error("Post-upload metadata fetch failed (non-fatal):", postUploadErr);
      }
    } catch (err) {
      // Surface as much detail as Firebase gives us — the generic
      // "An unknown error occurred, please check error payload for server response"
      // hides the real cause inside `serverResponse` / `customData`.
      const serverResponse =
        err?.customData?.serverResponse || err?.serverResponse || null;
      const code = err?.code || null;
      const status = err?.status_ || err?.status || null;
      console.error("Upload error:", err, { code, status, serverResponse });
      setFirebaseUploadStatus("failed");
      const detail = [code, status, serverResponse, err?.message]
        .filter(Boolean)
        .join(" · ");
      setError(detail ? `Upload failed: ${detail}` : "Upload failed. Please try again.");
      setPhase("reviewing");
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 30,
        background: "#000",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Map overlay (Hidden / Small / Large).
          Skipped while recording — that phase renders its own contained map PIP
          (bottom-right). Rendering this one too let Leaflet's high-z-index marker
          pane bleed over the recording controls (blue dot over the ENCRYPTED badge). */}
      {mapOverlaySize !== "hidden" && phase !== "recording" && (
        <div style={mapOverlayStyle}>
          <MapContainer
            key={`video-map-${mapOverlaySize}`}
            center={mapCenter}
            zoom={14}
            zoomControl={false}
            attributionControl={false}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
            {location && (
              <Marker position={[location.lat, location.lng]} icon={pulseIcon} />
            )}
            <MapResizeController overlaySize={mapOverlaySize} />
            <RecenterMap lat={location?.lat} lng={location?.lng} />
          </MapContainer>
        </div>
      )}

      {phase === "preview" && (
        <>
          {/* Preview overlays on top of the persistent map */}
          <div style={{ flex: 1, position: "relative", zIndex: 1, overflow: "hidden" }}>

            <video
              ref={previewVideoRef}
              autoPlay
              playsInline
              muted
              poster="/the99-logo.png"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 900, background: "#000" }}
            />

            {previewBlocked && phase === "preview" && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  background: "rgba(0,0,0,0.6)",
                  zIndex: 1003,
                }}
              >
                <button
                  type="button"
                  onClick={enablePreview}
                  style={{
                    padding: "12px 18px",
                    borderRadius: 10,
                    border: `1px solid ${OVERLAY.seafoam}`,
                    background: "rgba(0,0,0,0.75)",
                    color: OVERLAY.text,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Tap to enable preview
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: `1px solid ${OVERLAY.muted}66`,
                    background: "transparent",
                    color: OVERLAY.muted,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  ← Back
                </button>
              </div>
            )}

            {/* Subtle dark overlay for badge readability */}
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.28)", zIndex: 1000, pointerEvents: "none" }} />

            {/* Pre-record hint watermark — fades out once recording starts.
                Tells the angler to press the red button to begin. */}
            {!previewBlocked && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 1001,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 14,
                  pointerEvents: "none",
                  animation: "proofHintPulse 2.4s ease-in-out infinite",
                }}
              >
                <style>{`
                  @keyframes proofHintPulse { 0%,100% { opacity: 0.32; } 50% { opacity: 0.6; } }
                  @keyframes proofHintArrow { 0%,100% { transform: translateY(0); } 50% { transform: translateY(7px); } }
                `}</style>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 800,
                    letterSpacing: 2,
                    color: "#fff",
                    textAlign: "center",
                    textShadow: "0 1px 6px rgba(0,0,0,0.85)",
                    textTransform: "uppercase",
                  }}
                >
                  Aim the video
                </div>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    border: "2.5px solid rgba(255,255,255,0.85)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.red }} />
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    letterSpacing: 1.5,
                    color: "#fff",
                    textAlign: "center",
                    textShadow: "0 1px 6px rgba(0,0,0,0.8)",
                    textTransform: "uppercase",
                  }}
                >
                  Press the red button
                  <br />
                  to start recording
                </div>
                <div
                  style={{
                    fontSize: 26,
                    color: "#fff",
                    textShadow: "0 1px 6px rgba(0,0,0,0.8)",
                    animation: "proofHintArrow 1.4s ease-in-out infinite",
                  }}
                >
                  ↓
                </div>
              </div>
            )}

            {/* Corner bracket SVG */}
            <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1001, opacity: 0.55, pointerEvents: "none" }}>
              <path d="M22 46 L22 22 L46 22" stroke={C.accent} strokeWidth={2.5} fill="none" strokeLinecap="round" />
              <path d="M329 22 L353 22 L353 46" stroke={C.accent} strokeWidth={2.5} fill="none" strokeLinecap="round" />
              <path d="M22 560 L22 584 L46 584" stroke={C.accent} strokeWidth={2.5} fill="none" strokeLinecap="round" />
              <path d="M329 584 L353 584 L353 560" stroke={C.accent} strokeWidth={2.5} fill="none" strokeLinecap="round" />
            </svg>

            {/* TOURNAMENT MODE badge + close */}
            <div
              style={{
                position: "absolute",
                top: 14,
                left: 14,
                right: 14,
                zIndex: 1002,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  background: "rgba(0,0,0,0.85)",
                  borderRadius: 8,
                  padding: "6px 11px",
                  border: `1px solid ${OVERLAY.gold}55`,
                  textShadow: OVERLAY.textShadow,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <ProfileAvatar
                  url={getProfilePhotoUrl(userProfile)}
                  name={[userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(" ")}
                  size={22}
                />
                <div>
                  {userProfile?.tournamentId && (
                    <div style={{ fontSize: 8, color: OVERLAY.gold, letterSpacing: 2, marginBottom: 1 }}>TOURNAMENT MODE</div>
                  )}
                  <div style={{ fontSize: 11, color: OVERLAY.text, fontWeight: 800, letterSpacing: 1 }}>PROOF OF CATCH</div>
                </div>
              </div>
            </div>

            {/* GPS unavailable hint */}
            {!location && (
              <div style={{
                position: "absolute",
                bottom: 100,
                left: 0,
                right: 0,
                textAlign: "center",
                fontSize: 10,
                color: OVERLAY.muted,
                zIndex: 1002,
                pointerEvents: "none",
              }}>
                Acquiring GPS…
              </div>
            )}

            <MetaStamp catchData={catchData} location={location} />
            {renderZoomControls()}
          </div>
          <div
            style={{
              background: "#080808",
              padding: "18px 28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ fontSize: 10, color: OVERLAY.muted, lineHeight: 1.5, maxWidth: 120 }}>
              GPS + metadata
              <br />
              stamped to video
            </div>
            <button
              id="video-start-recording"
              data-testid="video-start-recording"
              onClick={startRecording}
              type="button"
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "transparent",
                border: "3px solid #fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 0 0 6px rgba(255,255,255,0.06)",
              }}
            >
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  background: C.red,
                  boxShadow: `0 0 22px ${C.red}99`,
                }}
              />
            </button>
            <div style={{ fontSize: 10, color: OVERLAY.muted, lineHeight: 1.5, maxWidth: 120, textAlign: "right" }}>
              Certificate
              <br />
              on stop
            </div>
          </div>
          {error && (
            <div style={{ fontSize: 11, color: C.warning, padding: "10px 20px" }}>{error}</div>
          )}
        </>
      )}

      {phase === "recording" && (
        <>
          <Viewfinder>
            <video
              ref={recordingVideoRef}
              autoPlay
              playsInline
              muted
              onLoadedData={() => setRecordingVideoReady(true)}
              onPlaying={() => setRecordingVideoReady(true)}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#000", opacity: recordingVideoReady ? 1 : 0, transition: "opacity 120ms ease" }}
            />
            {previewBlocked && phase === "recording" && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.6)",
                  zIndex: 20,
                }}
              >
                <button
                  type="button"
                  onClick={enablePreview}
                  style={{
                    padding: "12px 18px",
                    borderRadius: 10,
                    border: `1px solid ${C.seafoam}`,
                    background: "rgba(0,0,0,0.75)",
                    color: C.white,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Tap to enable preview
                </button>
              </div>
            )}
            <div
              style={{
                position: "absolute",
                top: 14,
                left: 14,
                right: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(0,0,0,0.85)",
                  borderRadius: 20,
                  padding: "6px 13px",
                  textShadow: OVERLAY.textShadow,
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: OVERLAY.red,
                    opacity: 0.75,
                  }}
                />
                <span style={{ fontSize: 13, color: OVERLAY.text, fontWeight: 800, letterSpacing: 2 }}>
                  {fmt(recTime)}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                <div
                  style={{
                    background: "rgba(0,0,0,0.85)",
                    borderRadius: 8,
                    padding: "5px 11px",
                    border: `1px solid ${OVERLAY.seafoam}55`,
                    textShadow: OVERLAY.textShadow,
                  }}
                >
                  <span style={{ fontSize: 9, color: OVERLAY.seafoam, letterSpacing: 1 }}>🔒 ENCRYPTED</span>
                </div>
                <div style={{ display: "inline-flex", background: "rgba(0,0,0,0.62)", border: "1px solid #ffffff2e", borderRadius: 999, padding: 2 }}>
                  {[
                    { value: "hidden", label: "Off" },
                    { value: "small", label: "S" },
                    { value: "large", label: "L" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setMapOverlaySize(opt.value)}
                      title={`Map ${opt.value}`}
                      style={{
                        minWidth: 30,
                        height: 24,
                        border: "none",
                        borderRadius: 999,
                        background: mapOverlaySize === opt.value ? "rgba(46,191,165,0.28)" : "transparent",
                        color: mapOverlaySize === opt.value ? OVERLAY.text : OVERLAY.muted,
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                bottom: 14,
                left: 14,
                right: 14,
                background: "rgba(0,0,0,0.9)",
                borderRadius: 10,
                padding: "9px 12px",
                border: `1px solid ${OVERLAY.red}44`,
                textShadow: OVERLAY.textShadow,
              }}
            >
              <div style={{ fontSize: 8, color: OVERLAY.red, letterSpacing: 2, marginBottom: 4 }}>
                ● LIVE STAMP · TAMPER-EVIDENT
              </div>
              <div style={{ display: "flex", gap: 20 }}>
                <div>
                  <span style={{ fontSize: 8, color: OVERLAY.muted }}>GPS: </span>
                  <span style={{ fontSize: 9, color: OVERLAY.text, fontWeight: 700 }}>
                    {location ? `${location.lat.toFixed(5)}°, ${location.lng.toFixed(5)}°` : "--"}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: 8, color: OVERLAY.muted }}>TIME: </span>
                  <span style={{ fontSize: 9, color: OVERLAY.text, fontWeight: 700 }}>{formatTimestamp()}</span>
                </div>
              </div>
            </div>
            {renderZoomControls()}
          </Viewfinder>

          {/* Map PIP — responds to Off / Small / Large toggle while recording */}
          {mapOverlaySize !== "hidden" && (
            <div
              style={{
                position: "absolute",
                bottom: 80,
                right: 14,
                width: mapOverlaySize === "large" ? 156 : 96,
                height: mapOverlaySize === "large" ? 156 : 96,
                borderRadius: 8,
                overflow: "hidden",
                border: `2px solid ${C.accent}88`,
                zIndex: 15,
                boxShadow: "0 2px 12px rgba(0,0,0,0.7)",
              }}
            >
              <MapContainer
                key={`recording-map-pip-${mapOverlaySize}`}
                center={mapCenter}
                zoom={14}
                zoomControl={false}
                attributionControl={false}
                style={{ width: "100%", height: "100%", pointerEvents: "none" }}
              >
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
                {location && (
                  <Marker position={[location.lat, location.lng]} icon={pulseIcon} />
                )}
                <MapResizeController overlaySize={mapOverlaySize} />
                <RecenterMap lat={location?.lat} lng={location?.lng} />
              </MapContainer>
            </div>
          )}
          {previewBlocked && phase === "recording" && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.6)",
                zIndex: 20,
              }}
            >
              <button
                type="button"
                onClick={enablePreview}
                style={{
                  padding: "12px 18px",
                  borderRadius: 10,
                  border: `1px solid ${OVERLAY.seafoam}`,
                  background: "rgba(0,0,0,0.75)",
                  color: OVERLAY.text,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Tap to enable preview
              </button>
            </div>
          )}
          <div
            style={{
              background: "#080808",
              padding: "18px 28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ fontSize: 10, color: OVERLAY.muted, lineHeight: 1.5 }}>
              Streaming to
              <br />
              secure cloud…
            </div>
            <button
              id="video-stop-recording"
              data-testid="video-stop-recording"
              onClick={stopRecording}
              type="button"
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "transparent",
                border: `3px solid ${C.red}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 5,
                  background: C.red,
                  boxShadow: `0 0 18px ${C.red}99`,
                }}
              />
            </button>
            <div style={{ fontSize: 10, color: OVERLAY.muted, lineHeight: 1.5, textAlign: "right" }}>
              Tap square
              <br />
              to stop
            </div>
          </div>
        </>
      )}

      {phase === "reviewing" && (
        <div style={{ flex: 1, overflowY: "auto", background: C.deepWater, padding: "20px 18px" }}>
          <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 3 }}>VIDEO REVIEW</div>
          <div style={{ fontSize: 19, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700, marginBottom: 16 }}>
            Confirm & Certify
          </div>

          <div
            style={{
              borderRadius: 12,
              overflow: "hidden",
              aspectRatio: "16 / 9",
              background: "#000",
              position: "relative",
              marginBottom: 14,
              border: `1px solid ${C.border}`,
            }}
          >
            <video
              ref={reviewVideoRef}
              playsInline
              preload="auto"
              poster="/the99-logo.png"
              onClick={toggleReviewPlay}
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000", cursor: "pointer" }}
            />
            <div style={{ position: "absolute", top: 10, right: 12 }}>
              <Badge color={C.seafoam} style={{ fontSize: 8 }}>
                ENCRYPTED · READY
              </Badge>
            </div>
            {/* Custom control bar — scaled to the known recorded duration so the
                scrubber tracks correctly even for long WebM clips (which omit
                duration metadata). */}
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                padding: "10px 12px 9px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 100%)",
              }}
            >
              <button
                type="button"
                onClick={toggleReviewPlay}
                aria-label={reviewPlaying ? "Pause" : "Play"}
                style={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  fontSize: 13,
                  lineHeight: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {reviewPlaying ? "❚❚" : "►"}
              </button>
              <div
                onClick={seekReviewFromEvent}
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.25)",
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${reviewDurationSec > 0 ? Math.max(0, Math.min(100, (reviewCurrentSec / reviewDurationSec) * 100)) : 0}%`,
                    background: C.seafoam,
                    borderRadius: 3,
                  }}
                />
              </div>
              <span style={{ fontSize: 10, color: "#fff", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                {fmt(Math.floor(reviewCurrentSec))} / {fmt(Math.floor(reviewDurationSec))}
              </span>
            </div>
          </div>

          <Card style={{ padding: "12px 14px", marginBottom: 14, borderColor: C.gold + "44" }}>
            <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, marginBottom: 8 }}>
              ATTACHED METADATA · TAMPER-EVIDENT
            </div>
            {[
              ["GPS", location ? `${location.lat.toFixed(5)}°N · ${location.lng.toFixed(5)}°W` : "—"],
              ["Timestamp", formatTimestamp()],
              ["Session ID", certId],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "5px 0",
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <span style={{ fontSize: 10, color: C.muted }}>{k}</span>
                <span
                  style={{
                    fontSize: 10,
                    color: C.white,
                    fontWeight: 600,
                    textAlign: "right",
                    maxWidth: 190,
                  }}
                >
                  {v}
                </span>
              </div>
            ))}
          </Card>

          <Card
            style={{
              padding: "12px 14px",
              marginBottom: 14,
              borderColor: clipOverLimit ? `${C.warning}88` : `${C.seafoam}44`,
              background: clipOverLimit ? "rgba(224,123,57,0.10)" : "rgba(37,99,235,0.06)",
            }}
          >
            <div style={{ fontSize: 9, color: clipOverLimit ? C.warning : C.seafoam, letterSpacing: 2, marginBottom: 8 }}>
              {isGoProSource ? "GOPRO UPLOAD" : "UPLOAD"} · {isProAngler ? "PRO ANGLER" : "ANGLER"}
            </div>
            {[
              ["Clip size", clipSizeBytes > 0 ? formatBytes(clipSizeBytes) : "—"],
              [`Your limit${isGoProSource ? " (GoPro)" : ""}`, formatBytes(uploadSizeCapBytes)],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "5px 0",
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <span style={{ fontSize: 10, color: C.muted }}>{k}</span>
                <span
                  style={{
                    fontSize: 10,
                    color: k === "Clip size" && clipOverLimit ? C.warning : C.white,
                    fontWeight: 600,
                    textAlign: "right",
                  }}
                >
                  {v}
                </span>
              </div>
            ))}
            {clipOverLimit && (
              <div style={{ fontSize: 10, color: C.warning, lineHeight: 1.4, marginTop: 8 }}>
                {isProAngler
                  ? `Over the ${formatBytes(uploadSizeCapBytes)} limit — trim the clip and try again.`
                  : `Over the ${formatBytes(uploadSizeCapBytes)} limit. Upgrade to Pro for uploads up to ${formatBytes(VIDEO_SIZE_CAPS[`${isGoProSource}:true`])}.`}
              </div>
            )}
          </Card>

          {error && (
            <div style={{ fontSize: 11, color: C.warning, marginBottom: 12, textAlign: "center" }}>{error}</div>
          )}

          {monthlyLimitReached && (
            <Card style={{ padding: "12px 14px", marginBottom: 12, borderColor: `${C.gold}66`, background: "rgba(224,123,57,0.10)" }}>
              <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, marginBottom: 6 }}>MONTHLY VIDEO LIMIT REACHED</div>
              <div style={{ fontSize: 11, color: C.white, lineHeight: 1.5 }}>
                New anglers can post <strong>one catch video per month</strong>. Keep logging your catches — they&rsquo;re unlimited. Become a <strong>Pro Angler</strong> to post more videos.
              </div>
            </Card>
          )}

          <Button
            id="video-upload-certify"
            data-testid="video-upload-certify"
            disabled={clipOverLimit || monthlyLimitReached}
            onClick={() => {
              if (clipOverLimit || monthlyLimitReached) return;
              if (!location) {
                setShowNoGpsConfirm(true);
                return;
              }
              uploadRecording();
            }}
            variant="primary"
            fullWidth
          >
            Upload & Certify
          </Button>
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            {sourceLabel !== "gopro" && (
              <Button
                id="video-rerecord"
                data-testid="video-rerecord"
                onClick={() => {
                  if (videoUrl) {
                    try { URL.revokeObjectURL(videoUrl); } catch {}
                  }
                  setVideoUrl(null);
                  recordedBlobsRef.current = [];
                  startRecording();
                }}
                variant="secondary"
                style={{ flex: 1 }}
              >
                Re-record
              </Button>
            )}
            <Button
              id="video-cancel"
              data-testid="video-cancel"
              onClick={onClose}
              variant="ghost"
              style={{ flex: 1 }}
            >
              Cancel
            </Button>
          </div>
          {!location && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 10px",
                background: "rgba(224,123,57,0.12)",
                border: `1px solid ${C.warning}66`,
                borderRadius: 8,
                fontSize: 11,
                color: C.warning,
                lineHeight: 1.4,
              }}
            >
              <strong style={{ letterSpacing: 1 }}>NO GPS FIX</strong> · This video will be uploaded without a location. Catches recorded from this video won't be plotted on the map.
            </div>
          )}
          {sourceLabel === "gopro" && onBackToGoPro && (
            <Button
              onClick={onBackToGoPro}
              variant="secondary"
              fullWidth
              style={{ marginTop: 16 }}
            >
              ← Back to GoPro list
            </Button>
          )}
        </div>
      )}

      {phase === "uploading" && (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: C.deepWater,
            padding: 28,
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: `${C.teal}22`,
              border: `2px solid ${C.teal}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20,
              fontSize: 30,
            }}
          >
            ☁
          </div>
          <div
            style={{
              fontSize: 15,
              color: C.white,
              fontFamily: "Georgia,serif",
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            Securing to Cloud
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.muted,
              marginBottom: 26,
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            Encrypting video + metadata
            <br />
            Generating tamper-evident hash
            <br />
            Writing to Firebase Storage…
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: 280,
              height: 6,
              background: "#1e3448",
              borderRadius: 3,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                height: 6,
                width: `${uploadPct}%`,
                background: `linear-gradient(90deg,${C.teal},${C.accent})`,
                borderRadius: 3,
                transition: "width 0.2s ease",
              }}
            />
          </div>
          <div style={{ fontSize: 14, color: C.accent, fontWeight: 800 }}>{Math.round(uploadPct)}%</div>
        </div>
      )}

      {phase === "saved-offline" && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 24px", background: C.deepWater, gap: 16 }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "rgba(46,191,165,0.12)", border: `2px solid ${C.accent}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ fontSize: 28 }}>📵</span>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 18, color: C.white, fontWeight: 700, marginBottom: 8 }}>Saved Offline</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, maxWidth: 260 }}>
              Your video has been saved to this device. It will automatically upload to The 99 when you're back online.
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="primary"
            style={{ marginTop: 8 }}
          >
            Done
          </Button>
        </div>
      )}

      {phase === "certified" && (
        <div style={{ flex: 1, overflowY: "auto", background: C.deepWater, padding: "22px 18px" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: `${C.seafoam}22`,
                border: `2px solid ${C.seafoam}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 14px",
              }}
            >
              <span style={{ fontSize: 26, color: C.accent }}>✓</span>
            </div>
            <div
              style={{
                fontSize: 20,
                color: C.white,
                fontFamily: "Georgia,serif",
                fontWeight: 700,
              }}
            >
              Catch Certified
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              Tamper-evident certificate ready to share
            </div>
          </div>

          <div
            style={{
              background: `linear-gradient(135deg,${C.midnight},${C.cardBg})`,
              borderRadius: 14,
              padding: "18px 16px",
              marginBottom: 14,
              border: `1px solid ${C.gold}66`,
              boxShadow: `0 0 32px ${C.gold}18`,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, marginBottom: 2 }}>
                  THE 99 · PROOF OF CATCH
                </div>
                <div
                  style={{
                    fontSize: 17,
                    color: C.white,
                    fontFamily: "Georgia,serif",
                    fontWeight: 700,
                  }}
                >
                  {catchData.species}
                </div>
                <div style={{ fontSize: 12, color: C.sand }}>
                  {catchData.size} · {catchData.weight}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 8, color: C.muted, marginBottom: 2 }}>CERT ID</div>
                <div
                  style={{
                    fontSize: 9,
                    color: C.gold,
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  {certId}
                </div>
              </div>
            </div>
            {timeTamperFlag && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#3d1a00",
                border: "1px solid #e07b3988",
                borderRadius: 8,
                padding: "7px 10px",
                marginBottom: 8,
              }}>
                <span style={{ fontSize: 14 }}>⚠️</span>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.warning }}>Clock Mismatch Detected</div>
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 1 }}>
                    Device time was {Math.round(timeDriftSeconds)}s off at certification.
                    This cert is flagged for director review.
                  </div>
                </div>
              </div>
            )}
            <div style={{ height: 1, background: "#1e3448", marginBottom: 10 }} />
            {[
              ["Timestamp", formatTimestamp()],
              ["GPS", location ? `${location.lat.toFixed(5)}°N · ${location.lng.toFixed(5)}°W` : "–"],
              ["Storage", "Firebase Storage · Encrypted"],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "4px 0",
                }}
              >
                <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>{k}</span>
                <span
                  style={{
                    fontSize: 12,
                    color: C.white,
                    textAlign: "right",
                    maxWidth: 195,
                  }}
                >
                  {v}
                </span>
              </div>
            ))}

          </div>

          <Card style={{ padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 9, color: C.teal, letterSpacing: 2, marginBottom: 8 }}>ON THE 99</div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
              <span style={{ fontSize: 11, color: C.muted }}>Certified upload</span>
              <span style={{ fontSize: 11, color: C.seafoam, fontWeight: 700 }}>
                {firebaseUploadStatus === "confirmed"
                  ? "Certified ✓"
                  : firebaseUploadStatus === "uploading"
                  ? "Uploading"
                  : firebaseUploadStatus === "failed"
                  ? "Failed"
                  : "Pending"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
              <span style={{ fontSize: 11, color: C.muted }}>Views</span>
              <span style={{ fontSize: 11, color: C.white, fontWeight: 700 }}>👁 {videoViewCount}</span>
            </div>

            {youtubeOptIn && !isProAngler && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 9, color: C.teal, letterSpacing: 2, marginBottom: 4 }}>SHARE THIS CATCH</div>
                <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>
                  Posting your certified clip to THE 99&rsquo;s Facebook &amp; YouTube pages is a Pro feature.
                  Upgrade to Pro Angler to share your catches.
                </div>
              </div>
            )}

            {youtubeOptIn && isProAngler && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 9, color: C.teal, letterSpacing: 2, marginBottom: 4 }}>SHARE THIS CATCH</div>
                <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4, marginBottom: 10 }}>
                  Your clip is certified on THE 99. Post it to THE 99&rsquo;s official pages &mdash; each is
                  confirmed by you, never automatic.
                </div>

                {/* Facebook */}
                <div style={{ marginBottom: 10 }}>
                  {facebookShareStatus === "confirmed" ? (
                    <div>
                      <div style={{ fontSize: 11, color: C.seafoam, fontWeight: 700, marginBottom: facebookPermalink ? 4 : 0 }}>
                        ✓ Posted to Facebook
                      </div>
                      {facebookPermalink && (
                        <a
                          href={facebookPermalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 10, color: "#7fb1ff", wordBreak: "break-all" }}
                        >
                          {facebookPermalink}
                        </a>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handlePublishToFacebook}
                      disabled={facebookShareStatus === "posting" || firebaseUploadStatus !== "confirmed"}
                      style={{
                        width: "100%",
                        padding: "11px 0",
                        borderRadius: 10,
                        background: facebookShareStatus === "posting" ? "#1e3a66" : "#1877F2",
                        border: "none",
                        cursor: facebookShareStatus === "posting" ? "default" : "pointer",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#FFFFFF",
                        opacity: facebookShareStatus === "posting" ? 0.85 : 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="#FFFFFF">
                        <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
                      </svg>
                      {facebookShareStatus === "posting" ? "Posting to Facebook…" : "Post to Facebook"}
                    </button>
                  )}
                  {facebookShareError && (
                    <div style={{ fontSize: 10, color: C.warning, marginTop: 6 }}>{facebookShareError}</div>
                  )}
                </div>

                {/* YouTube */}
                <div>
                  {youtubeShareStatus === "confirmed" ? (
                    <div>
                      <div style={{ fontSize: 11, color: C.seafoam, fontWeight: 700, marginBottom: youtubeUrl ? 4 : 0 }}>
                        ✓ Posted to YouTube
                      </div>
                      {youtubeUrl && (
                        <a
                          href={youtubeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 10, color: C.gold, wordBreak: "break-all" }}
                        >
                          {youtubeUrl}
                        </a>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handlePublishToYouTube}
                      disabled={youtubeShareStatus === "posting" || firebaseUploadStatus !== "confirmed"}
                      style={{
                        width: "100%",
                        padding: "11px 0",
                        borderRadius: 10,
                        background: youtubeShareStatus === "posting" ? "#7a1f1f" : "linear-gradient(135deg,#FF0000,#c40000)",
                        border: "none",
                        cursor: youtubeShareStatus === "posting" ? "default" : "pointer",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#FFFFFF",
                        opacity: youtubeShareStatus === "posting" ? 0.85 : 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <YtIcon w={18} bg="#FFFFFF" tri="#c40000" />
                      {youtubeShareStatus === "posting"
                        ? `Posting to YouTube${youtubePostingElapsed > 0 ? ` — ${youtubePostingElapsed}s` : ""}…`
                        : "Post to YouTube"}
                    </button>
                  )}
                  {youtubeShareStatus === "failed" && youtubeShareError && (
                    <div style={{ fontSize: 10, color: C.warning, marginTop: 6 }}>{youtubeShareError}</div>
                  )}
                </div>
              </div>
            )}
          </Card>


          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(secureVideoLink)}
              style={{
                flex: 1,
                padding: "11px 0",
                borderRadius: 10,
                background: `linear-gradient(135deg,${C.seafoam},${C.teal})`,
                border: "none",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                color: "#FFFFFF",
              }}
            >
              📋 Copy Link
            </button>
            <button
              type="button"
              onClick={async () => {
                const message = `Check out my certified catch video: ${secureVideoLink}`;
                // Prefer the native share sheet: it works on tablets (which have
                // no SMS app) and still surfaces Messages on phones. The bare
                // `sms:` scheme silently does nothing on a Wi-Fi-only tablet, so
                // only use it as a fallback, then clipboard as a last resort.
                if (typeof navigator !== "undefined" && navigator.share) {
                  try {
                    await navigator.share({ text: message });
                    return;
                  } catch (err) {
                    // User dismissed the share sheet — treat as done.
                    if (err && err.name === "AbortError") return;
                  }
                }
                try {
                  window.location.href = `sms:?body=${encodeURIComponent(message)}`;
                } catch {
                  try {
                    await navigator.clipboard.writeText(secureVideoLink);
                    alert("No messaging app available — the link was copied to your clipboard.");
                  } catch {
                    /* nothing else we can do */
                  }
                }
              }}
              style={{
                flex: 1,
                padding: "11px 0",
                borderRadius: 10,
                background: "transparent",
                border: `1px solid ${C.gold}66`,
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                color: C.gold,
              }}
            >
              💬 Text Link
            </button>
            <button
              type="button"
              onClick={() => {
                const subject = encodeURIComponent("The 99 - Catch Certified Video");
                const body = encodeURIComponent(`Secure video link:\n${secureVideoLink}`);
                window.location.href = `mailto:?subject=${subject}&body=${body}`;
              }}
              style={{
                flex: 1,
                padding: "11px 0",
                borderRadius: 10,
                background: "transparent",
                border: `1px solid ${C.gold}66`,
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                color: C.gold,
              }}
            >
              <span style={{ color: C.white }}>✉</span> Email Link
            </button>
          </div>

          {catchRef?.catchId && catchRef?.sessionId ? (
            <div
              style={{
                marginBottom: 8,
                padding: "12px 14px",
                borderRadius: 10,
                background: `${C.accent}14`,
                border: `1px solid ${C.accent}55`,
                color: C.accent,
                fontSize: 13,
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              ✓ Catch logged
            </div>
          ) : (
            <Button
              id="video-log-catch"
              data-testid="video-log-catch"
              onClick={() => onLogCatch(certId)}
              variant="primary"
              fullWidth
              style={{ marginBottom: 8 }}
            >
              🎙 Log Catch
            </Button>
          )}

          <Button
            onClick={onClose}
            variant="ghost"
            fullWidth
          >
            ← Return to Recording
          </Button>
        </div>
      )}

      {showNoGpsConfirm && (
        <div
          onClick={() => setShowNoGpsConfirm(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(4,8,15,0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.cardBg,
              border: `1px solid ${C.border}`,
              borderRadius: 14,
              padding: 20,
              maxWidth: 380,
              width: "100%",
              boxShadow: C.menuShadow,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>📍</span>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: C.warning,
                }}
              >
                No GPS fix
              </div>
            </div>
            <div style={{ color: C.white, fontSize: 14, marginBottom: 8 }}>
              This video will be uploaded without a location.
            </div>
            <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.4, marginBottom: 16 }}>
              Catches logged from this video won't be plotted on the map and any tournament rules requiring location proof will not be satisfied. You can wait for a GPS fix or proceed without one.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowNoGpsConfirm(false)}
                style={{
                  flex: 1,
                  padding: "11px 0",
                  borderRadius: 10,
                  background: "transparent",
                  border: `1px solid ${C.border}`,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: C.muted,
                }}
              >
                Wait for GPS
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNoGpsConfirm(false);
                  uploadRecording();
                }}
                style={{
                  flex: 1,
                  padding: "11px 0",
                  borderRadius: 10,
                  background: C.warning,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: "#fff",
                }}
              >
                Upload anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
